import { match } from "@codesandbox/states";
import { InitPitcher } from "components/Editor/InitPitcher";
import { ProgressLoadingScreen } from "components/Editor/ProgressLoadingScreen";
import { GlobalError } from "components/Error/GlobalError";
import { useEnvironmentInterface } from "environment-interface";
import { env } from "environment-interface/env";
import { PreviewProvider } from "features/preview/Feature";
import type { Editor } from "features/types/editor";
import { motion, useAnimation } from "framer-motion";
import { usePreviewOverlay } from "hooks/preview/usePreviewOverlay";
import { useTask } from "hooks/preview/useTask";
import { inferProjectForBranch } from "isomorphic/queries";
import { Icon, Stack, Text, Tooltip, styled } from "prism-react";
import {
  convertProjectQueryToBranchEditorData,
  importBranch,
  importReadOnlyBranch,
} from "queries/project";
import * as React from "react";
import { router } from "router";
import type { ToolbarPosition } from "types/preview";

import { Logo } from "components/Logo";
import { SessionMatcher } from "components/SessionMatcher";
import { csbApi } from "utils/csbApi";
import {
  calculateToolbarPosition,
  getClientPreference,
  setClientPreference,
  setOverlayPosition,
} from "utils/preview";
import { withPitcherUrlProtocol } from "utils/url";

const PreviewFrame: React.FC<{
  task: "TASK_RUNNING" | "TASK_STOPPED";
  branchId: string;
  src: string;
}> = ({ branchId, task, src }) => {
  const [overlayState, overlayPreview] = usePreviewOverlay(src);
  const firstTimeAnimation = React.useRef(true);
  const [toolbarRef, setToolbarRef] = React.useState<HTMLDivElement | null>(
    null,
  );
  const [blockIframe, setBlockIframe] = React.useState(false);

  const bodyRef = React.useRef(document.querySelector("body"));

  const [toolbarPosition, setToolbarPosition] = React.useState<
    ToolbarPosition | undefined
  >(undefined);
  const control = useAnimation();

  const handleRect = React.useCallback((node: HTMLDivElement) => {
    setToolbarRef(node);
  }, []);

  const { navigation } = useEnvironmentInterface();

  React.useEffect(
    function setInitialPosition() {
      if (!toolbarPosition && toolbarRef) {
        const elementRect = toolbarRef?.getBoundingClientRect();
        const preference = getClientPreference();

        setToolbarPosition(preference);

        /**
         * Initial animation
         */
        control.set({
          ...calculateToolbarPosition(preference, elementRect),
          visibility: "visible",
          y: elementRect.height * 2 * (preference.includes("TOP") ? -1 : 1),
        });
        control.start({ y: 0 });
      }
    },
    [toolbarPosition, toolbarRef],
  );

  React.useEffect(
    function repositionToolbar() {
      const elementRect = toolbarRef?.getBoundingClientRect();

      if (elementRect && toolbarPosition) {
        control.set(calculateToolbarPosition(toolbarPosition, elementRect));
      }
    },
    [task],
  );

  React.useEffect(
    function mouseMove() {
      const hideToolbar = () => {
        // it doesn't hide the toolbar for touch devices
        if (window.matchMedia("(hover: none) and (pointer: coarse)").matches) {
          return null;
        }

        const elementRect = toolbarRef?.getBoundingClientRect();

        if (elementRect && toolbarPosition?.includes("BOTTOM")) {
          control.start({ y: elementRect.height * 2 });
        } else if (elementRect && toolbarPosition?.includes("TOP")) {
          control.start({ y: -elementRect.height * 2 });
        }
      };

      if (toolbarPosition === overlayState.state || task === "TASK_STOPPED") {
        control.start({ y: 0 });

        return;
      }

      if (overlayState.state === "DOCUMENT_CLICK") {
        hideToolbar();

        return;
      }

      const timer = setTimeout(
        () => {
          hideToolbar();
          firstTimeAnimation.current = false;
        },
        firstTimeAnimation.current ? 3000 : 1000,
      );

      return () => {
        clearTimeout(timer);
      };
    },
    [task, toolbarPosition, overlayState],
  );

  return (
    <>
      <Frame ref={overlayPreview.previewRef} block={blockIframe} src={src} />
      <WrapperStack
        ref={handleRect}
        animate={control}
        dragConstraints={bodyRef}
        initial={{ visibility: "hidden" }}
        onDragEnd={(event) => {
          setBlockIframe(false);

          const position = setOverlayPosition(event);

          if (position && toolbarRef) {
            control.start(
              calculateToolbarPosition(
                position,
                toolbarRef?.getBoundingClientRect(),
              ),
            );

            setToolbarPosition(position);
            setClientPreference(position);
          }
        }}
        onDragStart={() => setBlockIframe(true)}
        drag
        dragSnapToOrigin
      >
        <StackButton css={{ cursor: "grab" }}>
          <Logo
            className="logo"
            css={{ color: "$neutral-fg-high", width: 12, height: 12 }}
          />
          <Icon className="drag" type="drag" />
        </StackButton>

        <Tooltip content="Open Editor">
          <span>
            <StackLink
              as="a"
              href={`/redirect-to-project-editor/${branchId}`}
              target="_blank"
            >
              <Icon size={5} type="fileHtml" />
            </StackLink>
          </span>
        </Tooltip>

        {task === "TASK_STOPPED" && (
          <Tooltip content="Reconnect">
            <span>
              <StackButton onClick={() => navigation.reload()}>
                <Stack
                  css={{ color: "$informative-error-base", px: "$3" }}
                  horizontal
                >
                  <Icon size={4} type="circleBang" />
                  <Text
                    css={{
                      color: "$informative-error-base",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Live preview disconnected
                  </Text>
                </Stack>
              </StackButton>
            </span>
          </Tooltip>
        )}
      </WrapperStack>
    </>
  );
};

const PreviewWithRunningTask: React.FC<{
  taskName: string;
  branchId: string;
  redirect: boolean;
}> = ({ taskName, branchId, redirect }) => {
  const task = useTask(taskName, redirect);

  const renderStartingTask = () => (
    <ProgressLoadingScreen
      steps={[{ text: "Connecting...", duration: 1000, percentage: 100 }]}
    />
  );

  // When redirecting we never show the preview
  if (redirect) {
    return match(task, {
      TASK_RUNNING: renderStartingTask,
      TASK_STARTING: renderStartingTask,
      TASK_STOPPED: renderStartingTask,
      ERROR: ({ error }) => <h1>ERROR: {error}</h1>,
    });
  }

  // Even though the task might stop here, we display the iframe as the overlay
  // will indicate that a reconnect is necessary
  const renderPreview = ({
    src = "",
    state,
  }: {
    src?: string;
    state: "TASK_RUNNING" | "TASK_STOPPED";
  }) => {
    return (
      <PreviewProvider origin={withPitcherUrlProtocol(src)}>
        <PreviewWrapper>
          <PreviewFrame branchId={branchId} src={src} task={state} />
        </PreviewWrapper>
      </PreviewProvider>
    );
  };

  return match(task, {
    TASK_STARTING: renderStartingTask,
    TASK_RUNNING: renderPreview,
    TASK_STOPPED: renderPreview,
    ERROR: ({ error }) => <h1>ERROR: {error}</h1>,
  });
};

const useProject = ({
  owner,
  repo,
  branch,
}: {
  owner: string;
  repo: string;
  branch: string;
}) => {
  const [data, setData] = React.useState<Editor | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const getProject = async () => {
      const inferredProject = await inferProjectForBranch(csbApi, {
        owner,
        repo,
        branch,
        workspaceId: null,
      });

      if (!inferredProject) {
        throw new Error("BRANCH_NOT_FOUND");
      }

      let project: Editor | null;

      if (inferredProject.branch) {
        project = convertProjectQueryToBranchEditorData(
          owner,
          repo,
          inferredProject,
        );
      } else {
        project = inferredProject.team
          ? await importBranch({
              owner,
              repo,
              branch,
              workspaceId: inferredProject.team.id,
            })
          : await importReadOnlyBranch({
              owner,
              repo,
              branch,
            });
      }

      return project;
    };

    getProject()
      .then((project) => {
        if (!project) {
          throw new Error("BRANCH_NOT_FOUND");
        }

        // Only do a server-side redirect in production, as we hardcode the preview url
        if (
          project &&
          env.PUBLIC_ENVIRONMENT === "production" &&
          router.queries.redirect &&
          router.queries.port
        ) {
          window.location.href = `https://${project.id}-${router.queries.port}.csb.app`;
          return;
        }

        setData(project);
      })
      .catch((error) => setError(error.message));
  }, []);

  return { data, error };
};

/**
 * This page has two purposes.
 * 1. It acts as a proxy to keep same origin when opening preview popups. This allows the parent to react when the popup is closed
 * 2. It is used with PR descriptions to ensure that the task responsible for the preview is actually running
 */
export default function Preview(props: {
  owner: string;
  repo: string;
  branch: string;
}) {
  const { data, error } = useProject(props);

  if (!router.queries.task) {
    return (
      <GlobalError
        context={{
          error: "No task defined in query",
          context: "Loading preview devtool",
        }}
        errorCode="PAGE_NOT_FOUND"
      />
    );
  }

  if (error) {
    return (
      <GlobalError
        context={{ error, context: "Loading preview devtool" }}
        errorCode="BRANCH_NOT_FOUND"
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <SessionMatcher>
      <InitPitcher id={data.id} seamlessFork={false}>
        <PreviewWithRunningTask
          branchId={data.id}
          redirect={Boolean(router.queries.redirect)}
          taskName={router.queries.task as string}
        />
      </InitPitcher>
    </SessionMatcher>
  );
}

const Frame = styled("iframe", {
  width: "100vw",
  height: "100vh",
  background: "white",
  outline: "none",
  border: "none",

  variants: {
    block: {
      true: {
        pointerEvents: "none",
      },
    },
  },
});

const PreviewWrapper = styled("div", {
  position: "relative",
});

const WrapperStack = styled(motion.div, {
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: "2147483647", // maximum range
  background: "$neutral-bg-medium",
  borderRadius: "$2",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",

  display: "flex",
  margin: 22,

  "> *:not(:first-child):not(:last-child)": {
    borderRight: "1px solid $neutral-bg-high",
  },
});

const StackButton = styled("a", {
  minWidth: 40,
  height: 40,
  display: "flex",
  color: "$neutral-fg-subtle",
  transition: "background $fast",
  position: "relative",

  ".drag": {
    position: "absolute",
    inset: 0,
    opacity: 0,
  },

  /* smartphones, touchscreens */
  "@media (hover: none) and (pointer: coarse)": {
    ".drag": {
      opacity: 1,
    },
    ".logo": {
      opacity: 0,
    },
  },

  "&:hover": {
    ".drag": {
      opacity: 1,
    },
    ".logo": {
      opacity: 0,
    },
  },

  "*": { margin: "auto", transition: "opacity $fast" },
});

const StackLink = styled(StackButton, {
  "&:hover": {
    background: "$neutral-bg-focus",
  },
});
